import React, { useState, useRef, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import ChatBubble from './ChatBubble';
import ToastHost  from './ToastHost';

export default function Layout({ children, currentUser, currentRoute, onNavigate, onLogout, notifications, onMarkRead, pageNeon, navMode, onNavModeChange }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deployedAt, setDeployedAt] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);

  /* Fetch last deployment timestamp once on mount */
  useEffect(() => {
    fetch('/api/version')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.deployedAt) setDeployedAt(d.deployedAt); })
      .catch(() => {});
  }, []);

  /* Resolve the topbar avatar URL. /api/auth/me already returns photoPath
     (a server-relative URL like /uploads/employee-photos/abc.jpg) when
     the logged-in user has a linked employee row with a photo, so we
     prefer that. Falls back to other plausible field names just in case
     a different code path hydrated currentUser. */
  useEffect(() => {
    if (!currentUser) { setUserPhoto(null); return; }
    const fromAuthMe = currentUser.photoPath || currentUser.photo_path
                      || currentUser.photo  || currentUser.avatar;
    if (fromAuthMe) { setUserPhoto(fromAuthMe); return; }
    setUserPhoto(null);
  }, [currentUser]);

  /* Format a UTC ISO string as IST (dd MMM yyyy HH:mm) */
  function formatIST(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const fmt = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(d);
      return fmt + ' IST';
    } catch { return ''; }
  }
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  /* Detect mobile / tablet breakpoint */
  const checkMobile = useCallback(() => {
    const mobile = window.innerWidth <= 1024;
    setIsMobile(mobile);
    if (mobile) setSidebarCollapsed(true);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  /* Close dropdowns on outside click */
  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggleSidebar() {
    setSidebarCollapsed(!sidebarCollapsed);
  }

  function handleNavigation(route) {
    onNavigate(route);
    // Auto-close sidebar on mobile after navigation
    if (isMobile) setSidebarCollapsed(true);
  }

  const initials = currentUser
    ? `${(currentUser.first_name || '')[0] || ''}${(currentUser.last_name || '')[0] || ''}`.toUpperCase()
    : '?';

  const displayName = currentUser
    ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
    : 'User';

  const roleName = currentUser?.role
    ? currentUser.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '';

  const unreadCount = (notifications || []).filter(n => !n.read).length;

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {isMobile && !sidebarCollapsed && (
        <div className="sidebar-overlay visible" onClick={() => setSidebarCollapsed(true)} />
      )}

      {/* Sidebar only renders in Classic mode. Nouveau is the tile-launchpad
          experience — the launchpad IS the navigation, plus the header toggle
          lets users flip back to Classic any time. */}
      {navMode !== 'nouveau' && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          currentRoute={currentRoute}
          onNavigate={handleNavigation}
        />
      )}

      <div className="main-content">
        {/* Top Bar */}
        <header className="topbar">
          {/* Mobile hamburger \u2014 only meaningful in Classic mode where the
              sidebar is the primary navigation. In Nouveau mode we replace it
              with a "Back to apps" button so the user always has a single-
              click route back to the launchpad without using browser-back. */}
          {navMode !== 'nouveau' && (
            <button className="mobile-hamburger" onClick={toggleSidebar} title="Toggle sidebar">
              {'\u2630'}
            </button>
          )}
          {navMode === 'nouveau' && currentRoute !== '/home' && (
            <button
              onClick={() => handleNavigation('/home')}
              title="Back to launchpad"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', marginRight: 8, fontSize: 12.5, fontWeight: 600,
                background: 'rgba(0,224,255,0.10)', color: '#6FE6FF',
                border: '1px solid rgba(0,224,255,0.30)', borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              {/* Inline house glyph \u2014 small SVG so it adapts to dark/light without an emoji. */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z"/>
              </svg>
              Apps
            </button>
          )}

          <div className="topbar-search">
            <span className="search-icon">{'\u{1F50D}'}</span>
            <input
              type="text"
              placeholder="Search leads, opportunities, customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="topbar-right">
            {/* Last deployment timestamp (IST) */}
            {deployedAt && (
              <div
                title={`Deployed: ${formatIST(deployedAt)}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 16,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontSize: 11, color: '#94A3B8',
                  fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto',
                  whiteSpace: 'nowrap',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <polyline points="12 7 12 12 15 14"/>
                </svg>
                <span>Deployed {formatIST(deployedAt)}</span>
              </div>
            )}

            {/* Notifications */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                className="topbar-btn"
                onClick={() => setNotifOpen(!notifOpen)}
                title="Notifications"
              >
                {'\u{1F514}'}
                {unreadCount > 0 && <span className="badge-dot"></span>}
              </button>

              {notifOpen && (
                <div className="user-dropdown" style={{ right: 0, width: 300 }}>
                  <div style={{ padding: '12px 16px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid var(--fiori-border)' }}>
                    Notifications
                    {unreadCount > 0 && (
                      <span className="badge badge-blue" style={{ marginLeft: 8 }}>{unreadCount}</span>
                    )}
                  </div>
                  {(!notifications || notifications.length === 0) ? (
                    <div style={{ padding: '20px 16px', color: '#94A3B8', fontSize: 13, textAlign: 'center' }}>
                      No new notifications
                    </div>
                  ) : (
                    notifications.map(n => (
                      <a
                        key={n.id}
                        href="#"
                        onClick={(e) => { e.preventDefault(); onMarkRead(n.id); }}
                        style={{
                          display: 'block', padding: '10px 16px', fontSize: 13,
                          background: n.read ? 'transparent' : 'rgba(59,141,248,0.06)',
                          borderBottom: '1px solid var(--fiori-border)'
                        }}
                      >
                        <div style={{ fontWeight: n.read ? 400 : 600 }}>{n.message}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{n.time}</div>
                      </a>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Classic ↔ Nouveau navigation-mode toggle. Two-segment pill;
                clicking the inactive segment flips the user preference and
                persists via /api/auth/me/preferences. Hidden when no user is
                loaded yet so it doesn't flash the default state at startup. */}
            {currentUser && (
              <div title="Switch navigation mode" style={{
                display: 'inline-flex', alignItems: 'center', height: 30,
                borderRadius: 999, padding: 2, marginRight: 8,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)', userSelect: 'none',
              }}>
                {[
                  { id: 'classic', label: 'Classic' },
                  { id: 'nouveau', label: 'Nouveau' },
                ].map(seg => {
                  const active = (navMode || 'classic') === seg.id;
                  return (
                    <button key={seg.id}
                      onClick={() => { if (!active && onNavModeChange) onNavModeChange(seg.id); }}
                      style={{
                        appearance: 'none', border: 'none', cursor: active ? 'default' : 'pointer',
                        padding: '4px 12px', fontSize: 11.5, fontWeight: 600, borderRadius: 999,
                        background: active ? 'linear-gradient(135deg, #00E0FF, #0EA5E9)' : 'transparent',
                        color: active ? '#0B1020' : '#94A3B8',
                        transition: 'background .15s, color .15s',
                      }}>
                      {seg.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* User Menu */}
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <div className="user-menu" onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                {userPhoto ? (
                  <img src={userPhoto} alt={displayName}
                    className="user-avatar"
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    onError={() => setUserPhoto(null)} />
                ) : (
                  <div className="user-avatar">{initials}</div>
                )}
                <div className="user-info">
                  <div className="name">{displayName}</div>
                  <div className="role">{roleName}</div>
                </div>
              </div>

              {userDropdownOpen && (
                <div className="user-dropdown">
                  <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('/admin'); setUserDropdownOpen(false); }}>
                    Administration
                  </a>
                  <div className="divider"></div>
                  <a href="#" onClick={(e) => { e.preventDefault(); onLogout(); }}>
                    Sign Out
                  </a>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="content-area" style={pageNeon ? { '--page-neon': pageNeon } : undefined}>
          {children}
        </div>
      </div>
      <ChatBubble currentUser={currentUser} />
      <ToastHost />
    </div>
  );
}
